<template>
  <div class="ak-infinate-scroll">
    <div class="ak-infinate-scroll__content">
      <slot />
      <div v-if="loading" class="ak-infinate-scroll__loading">
        <ak-loading-spinner size="small" />
      </div>
      <ak-intersection-observer class="h-100" @intersect="handleLoad" />
      <ak-resize-observer @notify="handleResize" />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue'
import ResizeObserver from '../resizeObserver/ResizeObserver.vue'
import IntersectionObserver from '../intersectionObserver/IntersectionObserver.vue'
import LoadingSpinner from '../loading/LoadingSpinner.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <ak-infinite-scroll @load="fetchMore" :is-disabled="scrollDisabled">
   *   <template>
   *      stuff...
   *   </template>
   * </ak-infinite-scroll>
   */
  name: 'AkInfiniteScroll',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [ResizeObserver.name]: ResizeObserver,
    [IntersectionObserver.name]: IntersectionObserver,
    [LoadingSpinner.name]: LoadingSpinner,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    isDisabled: {
      type: Boolean,
      default: false,
    },

    load: {
      type: Function,
      default: undefined,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['resize'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const listener = ref(null)
    const loading = ref(false)

    /**
     * @description Handles the loading of more data into the component
     * this is triggered once we reach the end of the list
     */
    const handleLoad = async () => {
      if (!props.isDisabled) {
        loading.value = true
        await props.load()
        loading.value = false
      }
    }

    /**
     * @param {Object} size - The width and height of the parent element
     * @description Emits the resize event when the parent container size
     * changes.
     */
    const handleResize = (size) => {
      emit('resize', size)
    }

    return {
      listener,
      loading,
      handleLoad,
      handleResize,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-infinate-scroll {
  overflow-y: scroll;
  position: relative;

  &.ak-media-manager__scroll {
    overflow-y: unset;
  }

  &__content {
    height: 100%;
    width: 100%;
    position: relative;
  }

  &__loading {
    display: flex;
    justify-content: center;
    color: $color-primary;
    margin-top: $spacing;
  }
}
</style>
